import React from "react";
import { Check, CircleDashed, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { num, PROPOSAL_STATUS } from "@/lib/sk";
import { ACTING_LABEL } from "./model";

/** The configurator's answer in one word – coloured only by what it means for the work. */
export function StatusPill({ status, short = false, className, testId }) {
  const s = PROPOSAL_STATUS[status] || PROPOSAL_STATUS.blokkert;
  return <span className={cn("v-badge", s.cls, className)} data-testid={testId} data-status={status}>{short ? s.short : s.label}</span>;
}

/** The type code as it is written in Skarpenord's documentation – base actuator bold, connection/conditions/accessories after. */
export function CodeLine({ code, baseCode, className, testId }) {
  if (!code) return <span className={cn("text-muted-foreground", className)} data-testid={testId}>Ingen aktuator foreslått</span>;
  const base = baseCode && code.startsWith(baseCode) ? baseCode : code;
  const rest = code.slice(base.length).trim();
  return <span className={cn("font-mono tabular-nums tracking-[-0.01em]", className)} data-testid={testId}><span className="font-semibold text-foreground">{base}</span>{rest ? <span className="text-foreground/60"> {rest}</span> : null}</span>;
}

const CHECK_ICON = { true: [Check, "text-[hsl(var(--v-ok))] bg-[hsl(var(--v-ok-bg))]"], false: [X, "text-[hsl(var(--v-blocked))] bg-[hsl(var(--v-blocked-bg))]"], null: [CircleDashed, "text-muted-foreground bg-black/[0.05]"] };

/** Every check the sizing made – ok, failed or «cannot be checked» – with the figures it used. Nothing is hidden behind a colour. */
export function Checks({ checks, testId = "proposal-checks" }) {
  if (!checks?.length) return null;
  return (
    <ul className="space-y-1.5" data-testid={testId}>
      {checks.map((c) => {
        const [Icon, cls] = CHECK_ICON[String(c.ok)];
        return (
          <li key={c.key} className="flex items-start gap-2.5 text-[13.5px]" data-testid={`${testId}-${c.key}`} data-ok={String(c.ok)}>
            <span className={cn("mt-[2px] flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full", cls)}><Icon className="h-3 w-3" strokeWidth={2.75} /></span>
            <div className="min-w-0"><div className="font-medium text-foreground/90">{c.label}</div><div className="k-meta">{c.detail}</div></div>
          </li>
        );
      })}
    </ul>
  );
}

export function ReasonList({ items, tone = "eng", testId }) {
  if (!items?.length) return null;
  const dot = tone === "blocked" ? "bg-[hsl(var(--v-blocked))]" : tone === "assumed" ? "bg-[hsl(var(--v-assumed))]" : "bg-[hsl(var(--v-eng))]";
  return <ul className="space-y-1.5" data-testid={testId}>{items.map((r, i) => <li key={i} className="flex gap-2.5 text-[13.5px] leading-[1.5] text-foreground/85"><span className={cn("mt-[8px] h-1.5 w-1.5 shrink-0 rounded-full", dot)} />{r}</li>)}</ul>;
}

const BOM_STATUS = { dokumentert: ["Dokumentert", "text-[hsl(var(--v-ok))]"], alternativer: ["Velg", "text-[hsl(var(--v-assumed))]"], udokumentert: ["Ikke dokumentert", "text-muted-foreground"] };

/** Bill of material for one proposal: a part number only where Skarpenord's option sheet gives one. */
export function BomList({ bom, qty = 1, testId = "proposal-bom" }) {
  if (!bom?.length) return <p className="text-[13.5px] text-muted-foreground" data-testid={`${testId}-empty`}>Ingen dokumenterte tilvalg for denne størrelsen – stykkliste settes av ingeniør.</p>;
  return (
    <ul className="divide-y divide-black/[0.04] rounded-[12px] border border-black/[0.06]" data-testid={testId}>
      {bom.map((ln) => {
        const [label, cls] = BOM_STATUS[ln.status] || BOM_STATUS.udokumentert;
        return (
          <li key={ln.key} className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 px-3 py-2 text-[13px]" data-testid={`${testId}-${ln.key}`} data-status={ln.status}>
            <div className="min-w-0"><div className="truncate text-foreground/90">{ln.name}</div>{ln.options?.length ? <div className="k-meta truncate">Alternativer: {ln.options.join(" · ")}</div> : null}<div className="k-meta truncate">{ln.basis}</div></div>
            <div className="text-right"><div className="font-mono tabular-nums">{ln.part || "–"}</div><div className={cn("text-[11.5px] font-medium", cls)}>{label}{qty > 1 ? ` · ${num((ln.qty || 1) * qty)} stk` : ""}</div></div>
          </li>
        );
      })}
    </ul>
  );
}

/** One line about the torque basis: BTO/ETC from the list, MAST as the guard, the safety factor the rule asks for. */
export function Requirement({ req, testId = "proposal-requirement" }) {
  if (!req) return null;
  return (
    <dl className="grid grid-cols-3 gap-x-4 gap-y-1 text-[13.5px]" data-testid={testId}>
      <div><dt className="k-label">BTO</dt><dd className="tabular">{req.bto != null ? `${num(req.bto)} Nm` : "–"}</dd></div>
      <div><dt className="k-label">ETC</dt><dd className="tabular">{req.etc != null ? `${num(req.etc)} Nm` : "–"}</dd></div>
      <div><dt className="k-label">MAST</dt><dd className="tabular">{req.mast != null ? `${num(req.mast)} Nm` : <span className="text-[hsl(var(--v-assumed))]">ikke oppgitt</span>}</dd></div>
      <div className="col-span-3 k-meta">{req.basis_label} · SF minimum {req.sf_min} → behov {num(req.need_open)} Nm åpne / {num(req.need_close)} Nm lukke</div>
    </dl>
  );
}

export function actingLabel(p) {
  return p?.acting ? ACTING_LABEL[p.acting] || p.acting : null;
}
